import type React from "react";
import {type ReactNode, useCallback, useRef} from "react";
import ReactHowler from "react-howler";

import {useCurrentUserSettings} from "~/api/hooks/useSettings";
import {cld} from "~/firebase/cloudinary";

import {useUserInteraction} from "../userInteraction/UserInteractionContext";
import {AudioContext} from "./AudioContext";

// The minimum time in milliseconds that must pass before another
// collision sound can be played. 50ms allows up to 20 sounds per second.
const COLLISION_SOUND_COOLDOWN_MS = 50;

/**
 * Provides a set of functions to play various sound effects throughout the application.
 * It manages sound loading, user volume settings, and throttles high-frequency
 * sounds like collisions to prevent audio artifacts.
 */
export const AudioProvider: React.FC<{children: ReactNode}> = ({children}) => {
  const {data: settings} = useCurrentUserSettings();
  const {hasUserInteracted} = useUserInteraction();

  // --- Refs to the ReactHowler components ---
  const hoverSoundRef = useRef<ReactHowler>(null);
  const clickSoundRef = useRef<ReactHowler>(null);
  const collisionSoundRef = useRef<ReactHowler>(null);
  const attractorImpulseSoundRef = useRef<ReactHowler>(null);
  const shardCollectedSoundRef = useRef<ReactHowler>(null);
  const newPersonalBestSoundRef = useRef<ReactHowler>(null);
  const playerDiedSoundRef = useRef<ReactHowler>(null);

  // --- Ref to manage sound throttling ---
  const lastCollisionPlayTime = useRef(0);

  /**
   * A generic helper function to play a sound from a ref.
   * It respects user interaction state and master volume settings.
   * @param ref - A React ref to the ReactHowler component.
   * @param volumeMultiplier - A factor (0.0 to 1.0) to scale the master volume.
   */
  const playSound = useCallback(
    (ref: React.RefObject<ReactHowler | null>, volumeMultiplier = 1.0) => {
      // Sounds should not play until the user has interacted with the page.
      if (!hasUserInteracted) return;

      const sound = ref.current?.howler;
      if (sound) {
        const baseVolume = settings?.soundVolume ?? 0;
        const finalVolume =
          baseVolume * Math.min(Math.max(volumeMultiplier, 0.0), 1.0);
        sound.volume(finalVolume);
        sound.play();
      }
    },
    [hasUserInteracted, settings?.soundVolume],
  );

  // --- Public Play Functions (Memoized for performance) ---

  const playHover = useCallback(() => playSound(hoverSoundRef), [playSound]);
  const playClick = useCallback(() => playSound(clickSoundRef), [playSound]);
  const playAttractorImpulse = useCallback(
    () => playSound(attractorImpulseSoundRef, 0.2),
    [playSound],
  );
  const playShardCollected = useCallback(
    () => playSound(shardCollectedSoundRef),
    [playSound],
  );
  const playNewPersonalBest = useCallback(
    () => playSound(newPersonalBestSoundRef),
    [playSound],
  );
  const playPlayerDied = useCallback(
    () => playSound(playerDiedSoundRef),
    [playSound],
  );

  /**
   * A throttled function to play collision sounds. It will only play if the cooldown
   * period has passed since the last collision sound was played.
   */
  const playCollision = useCallback(
    (volumeMultiplier = 1.0) => {
      if (!hasUserInteracted) return;

      const now = performance.now();

      // Check if enough time has passed since the last play call.
      if (now - lastCollisionPlayTime.current > COLLISION_SOUND_COOLDOWN_MS) {
        // If so, play the sound and update the timestamp.
        playSound(collisionSoundRef, volumeMultiplier);
        lastCollisionPlayTime.current = now;
      }
      // If not, do nothing, effectively throttling the sound.
    },
    [hasUserInteracted, playSound],
  );

  // The context value provided to child components.
  const contextValue = {
    playHover,
    playClick,
    playCollision,
    playAttractorImpulse,
    playShardCollected,
    playNewPersonalBest,
    playPlayerDied,
  };

  return (
    <AudioContext.Provider value={contextValue}>
      {children}

      {/* --- Howler Instances (The actual sound players) --- */}
      {/* These components are mounted but remain invisible. They just load the audio. */}

      <ReactHowler
        src={cld
          .video("274029__junggle__click-ambient-mute_hcq3ob")
          .quality("auto")
          .toURL()}
        format={["mp3"]}
        volume={settings?.soundVolume ?? 0}
        preload={true}
        playing={false}
        ref={hoverSoundRef}
      />
      <ReactHowler
        src={cld
          .video("497709__miksmusic__echo-pop-1_igijkt")
          .quality("auto")
          .toURL()}
        format={["mp3"]}
        volume={settings?.soundVolume ?? 0}
        preload={true}
        playing={false}
        ref={clickSoundRef}
      />
      <ReactHowler
        src={cld.video("sharp-pop-328170_d1btgl").quality("auto").toURL()}
        format={["mp3"]}
        // We set the base volume here, but the playSound function will override it.
        volume={settings?.soundVolume ?? 0}
        preload={true}
        playing={false}
        ref={collisionSoundRef}
      />
      <ReactHowler
        src={cld.video("woosh-ukulele").quality("auto").toURL()}
        format={["mp3"]}
        volume={settings?.soundVolume ?? 0}
        preload={true}
        playing={false}
        ref={attractorImpulseSoundRef}
      />
      <ReactHowler
        src={cld.video("fireworks-single-boom").quality("auto").toURL()}
        format={["mp3"]}
        volume={settings?.soundVolume ?? 0}
        preload={true}
        playing={false}
        ref={shardCollectedSoundRef}
      />
      <ReactHowler
        src={cld.video("single-firework-79814_g8jxrf").quality("auto").toURL()}
        format={["mp3"]}
        volume={settings?.soundVolume ?? 0}
        preload={true}
        playing={false}
        ref={newPersonalBestSoundRef}
      />
      <ReactHowler
        src={cld
          .video(
            "784768__akkingstudio__deathcrunch-gore-sfx-blood-and-bone_v0hyll",
          )
          .quality("auto")
          .toURL()}
        format={["mp3"]}
        volume={settings?.soundVolume ?? 0}
        preload={true}
        playing={false}
        ref={playerDiedSoundRef}
      />
    </AudioContext.Provider>
  );
};
